import React, { useState } from "react";
import { useTranslation } from "next-i18next";
import { AiOutlineLoading } from "react-icons/ai";
import { FaFileContract, FaBuilding, FaUser, FaCheckCircle, FaShieldAlt, FaDownload } from "react-icons/fa";
import { HiDocumentText } from "react-icons/hi";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import * as Sentry from "@sentry/nextjs";
import {
	Section,
	Container,
	HeroBanner,
	HeroContent,
	HeroRow,
	HeroImage,
	HeroText,
	Title,
	Description,
	DownloadLink,
	FormCard,
	FormSection,
	FormSectionTitle,
	FormGrid,
	InputWrapper,
	InputLabel,
	Input,
	ErrorMessage,
	SubmitButton,
	InfoBox,
	SuccessMessage,
} from "./styles";

// Validação de CNPJ
const isValidCNPJ = (cnpj) => {
	cnpj = cnpj.replace(/[^\d]/g, '');
	if (cnpj.length !== 14) return false;
	if (/^(\d)\1+$/.test(cnpj)) return false;

	let size = cnpj.length - 2;
	let numbers = cnpj.substring(0, size);
	let digits = cnpj.substring(size);
	let sum = 0;
	let pos = size - 7;

	for (let i = size; i >= 1; i--) {
		sum += numbers.charAt(size - i) * pos--;
		if (pos < 2) pos = 9;
	}

	let result = sum % 11 < 2 ? 0 : 11 - sum % 11;
	if (result !== parseInt(digits.charAt(0))) return false;

	size = size + 1;
	numbers = cnpj.substring(0, size);
	sum = 0;
	pos = size - 7;

	for (let i = size; i >= 1; i--) {
		sum += numbers.charAt(size - i) * pos--;
		if (pos < 2) pos = 9;
	}

	result = sum % 11 < 2 ? 0 : 11 - sum % 11;
	if (result !== parseInt(digits.charAt(1))) return false;

	return true;
};

// Validação de CEP
const isValidCEP = (cep) => {
	const cleanCEP = cep.replace(/[^\d]/g, '');
	return cleanCEP.length === 8;
};

// Schema factory that accepts translation function
const createSchema = (t) => z.object({
	razaoSocial: z.string().min(1, t("nda.form.validation.required")),
	nomeFantasia: z.string().min(1, t("nda.form.validation.required")),
	cnpj: z.string()
		.min(1, t("nda.form.validation.required"))
		.refine((val) => isValidCNPJ(val), t("nda.form.validation.invalidCNPJ")),
	cidade: z.string().min(1, t("nda.form.validation.required")),
	bairro: z.string().min(1, t("nda.form.validation.required")),
	endereco: z.string().min(1, t("nda.form.validation.required")),
	cep: z.string()
		.min(1, t("nda.form.validation.required"))
		.refine((val) => isValidCEP(val), t("nda.form.validation.invalidCEP")),
	nomeResponsavel: z.string().min(1, t("nda.form.validation.required")),
	emailResponsavel: z.string()
		.min(1, t("nda.form.validation.required"))
		.email(t("nda.form.validation.invalidEmail")),
});

// Máscara de CNPJ
const formatCNPJ = (value) => {
	const numbers = value.replace(/\D/g, '');
	return numbers
		.replace(/^(\d{2})(\d)/, '$1.$2')
		.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
		.replace(/\.(\d{3})(\d)/, '.$1/$2')
		.replace(/(\d{4})(\d)/, '$1-$2')
		.substring(0, 18);
};

// Máscara de CEP
const formatCEP = (value) => {
	const numbers = value.replace(/\D/g, '');
	return numbers.replace(/^(\d{5})(\d)/, '$1-$2').substring(0, 9);
};

export default function NDAForm() {
	const { t } = useTranslation();
	const [isSuccess, setIsSuccess] = useState(false);
	const [documentName, setDocumentName] = useState('');

	const schema = createSchema(t);

	const form = useForm({
		defaultValues: {
			razaoSocial: "",
			nomeFantasia: "",
			cnpj: "",
			cidade: "",
			bairro: "",
			endereco: "",
			cep: "",
			nomeResponsavel: "",
			emailResponsavel: "",
		},
		mode: "onTouched",
		resolver: zodResolver(schema),
	});

	const {
		register,
		handleSubmit: hookFormHandleSubmit,
		formState,
		setValue,
		watch,
	} = form;

	const handleCNPJChange = (e) => {
		const formatted = formatCNPJ(e.target.value);
		setValue('cnpj', formatted, { shouldValidate: true });
	};

	const handleCEPChange = (e) => {
		const formatted = formatCEP(e.target.value);
		setValue('cep', formatted, { shouldValidate: true });
	};

	const handleSubmit = async (data) => {
		try {
			const apiUrl = `${process.env.NEXT_PUBLIC_VERZEL_FUNCTIONS_HOST}/nda/send`;
			const response = await fetch(apiUrl, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					razaoSocial: data.razaoSocial,
					nomeFantasia: data.nomeFantasia,
					cnpj: data.cnpj,
					cidade: data.cidade,
					bairro: data.bairro,
					endereco: data.endereco,
					cep: data.cep,
					nomeResponsavel: data.nomeResponsavel,
					emailResponsavel: data.emailResponsavel,
				}),
			});

			const result = await response.json();

			if (response.ok && result.success) {
				setDocumentName(result.documentName || data.nomeFantasia);
				setIsSuccess(true);
				toast.success(t("nda.toast.success"));
			} else {
				// Tratar erros de validação com detalhes por campo
				if (result.details && Array.isArray(result.details)) {
					result.details.forEach((err) => {
						// Marcar erro no campo específico do formulário
						form.setError(err.field, { 
							type: 'server', 
							message: err.message 
						});
					});
					// Mostrar toast com resumo dos campos com erro
					const fieldNames = result.fields?.join(', ') || 'campos inválidos';
					toast.error(`${t("nda.toast.validationError") || "Erro de validação"}: ${fieldNames}`);
					
					// Log validation errors to Sentry
					Sentry.captureMessage('NDA form server validation error', {
						level: 'warning',
						tags: { form: 'nda', errorType: 'validation' },
						extra: { 
							validationErrors: result.details,
							fields: result.fields 
						}
					});
				} else {
					throw new Error(result.error || t("nda.toast.error"));
				}
			}
		} catch (error) {
			console.error('Erro ao enviar NDA:', error);
			Sentry.captureException(error, {
				tags: { form: 'nda' },
				extra: { 
					apiUrl: `${process.env.NEXT_PUBLIC_VERZEL_FUNCTIONS_HOST}/nda/send`,
					formData: {
						nomeFantasia: data.nomeFantasia,
						emailResponsavel: data.emailResponsavel,
					}
				}
			});
			toast.error(error.message || t("nda.toast.error"));
		}
	};

	if (isSuccess) {
		return (
			<Section style={{ paddingTop: '180px' }}>
				<Container>
					<FormCard>
						<SuccessMessage>
							<FaCheckCircle />
							<h2>{t("nda.success.title")}</h2>
							<p>{t("nda.success.message")}</p>
						</SuccessMessage>
					</FormCard>
				</Container>
			</Section>
		);
	}

	return (
		<Section>
			<Container className="container-blog">
				<HeroBanner>
					<HeroContent>
						<Title>{t("nda.hero.title")}</Title>
						<HeroRow>
							<HeroImage>
								<FaShieldAlt />
							</HeroImage>
							<HeroText>
								<Description>
									<p dangerouslySetInnerHTML={{ __html: t("nda.hero.description") }} />
								</Description>
								<DownloadLink
									href="/landings/assets/fles/NDA Verzel.docx"
									download="NDA Verzel - Modelo.docx"
								>
									<FaDownload />
									{t("nda.hero.downloadLink")}
								</DownloadLink>
							</HeroText>
						</HeroRow>
					</HeroContent>
				</HeroBanner>

				<FormCard>
					<form>
						<FormSection>
							<FormSectionTitle>
								<FaBuilding />
								{t("nda.form.sections.companyData")}
							</FormSectionTitle>
							<FormGrid columns={2}>
								<InputWrapper className={formState.errors.razaoSocial ? 'has-error' : ''}>
									<InputLabel>{t("nda.form.labels.razaoSocial")} *</InputLabel>
									<Input
										type="text"
										placeholder={t("nda.form.placeholders.razaoSocial")}
										className={formState.errors.razaoSocial ? 'error' : ''}
										{...register("razaoSocial")}
									/>
									{formState.errors.razaoSocial && (
										<ErrorMessage>{formState.errors.razaoSocial.message}</ErrorMessage>
									)}
								</InputWrapper>

								<InputWrapper className={formState.errors.nomeFantasia ? 'has-error' : ''}>
									<InputLabel>{t("nda.form.labels.nomeFantasia")} *</InputLabel>
									<Input
										type="text"
										placeholder={t("nda.form.placeholders.nomeFantasia")}
										className={formState.errors.nomeFantasia ? 'error' : ''}
										{...register("nomeFantasia")}
									/>
									{formState.errors.nomeFantasia && (
										<ErrorMessage>{formState.errors.nomeFantasia.message}</ErrorMessage>
									)}
								</InputWrapper>
							</FormGrid>

							<FormGrid columns={2} style={{ marginTop: 20 }}>
								<InputWrapper className={formState.errors.cnpj ? 'has-error' : ''}>
									<InputLabel>{t("nda.form.labels.cnpj")} *</InputLabel>
									<Input
										type="text"
										placeholder={t("nda.form.placeholders.cnpj")}
										className={formState.errors.cnpj ? 'error' : ''}
										{...register("cnpj")}
										onChange={handleCNPJChange}
										maxLength={18}
									/>
									{formState.errors.cnpj && (
										<ErrorMessage>{formState.errors.cnpj.message}</ErrorMessage>
									)}
								</InputWrapper>

								<InputWrapper className={formState.errors.cep ? 'has-error' : ''}>
									<InputLabel>{t("nda.form.labels.cep")} *</InputLabel>
									<Input
										type="text"
										placeholder={t("nda.form.placeholders.cep")}
										className={formState.errors.cep ? 'error' : ''}
										{...register("cep")}
										onChange={handleCEPChange}
										maxLength={9}
									/>
									{formState.errors.cep && (
										<ErrorMessage>{formState.errors.cep.message}</ErrorMessage>
									)}
								</InputWrapper>
							</FormGrid>

							<FormGrid style={{ marginTop: 20 }}>
								<InputWrapper className={formState.errors.endereco ? 'has-error' : ''}>
									<InputLabel>{t("nda.form.labels.endereco")} *</InputLabel>
									<Input
										type="text"
										placeholder={t("nda.form.placeholders.endereco")}
										className={formState.errors.endereco ? 'error' : ''}
										{...register("endereco")}
									/>
									{formState.errors.endereco && (
										<ErrorMessage>{formState.errors.endereco.message}</ErrorMessage>
									)}
								</InputWrapper>
							</FormGrid>

							<FormGrid columns={2} style={{ marginTop: 20 }}>
								<InputWrapper className={formState.errors.bairro ? 'has-error' : ''}>
									<InputLabel>{t("nda.form.labels.bairro")} *</InputLabel>
									<Input
										type="text"
										placeholder={t("nda.form.placeholders.bairro")}
										className={formState.errors.bairro ? 'error' : ''}
										{...register("bairro")}
									/>
									{formState.errors.bairro && (
										<ErrorMessage>{formState.errors.bairro.message}</ErrorMessage>
									)}
								</InputWrapper>

								<InputWrapper className={formState.errors.cidade ? 'has-error' : ''}>
									<InputLabel>{t("nda.form.labels.cidade")} *</InputLabel>
									<Input
										type="text"
										placeholder={t("nda.form.placeholders.cidade")}
										className={formState.errors.cidade ? 'error' : ''}
										{...register("cidade")}
									/>
									{formState.errors.cidade && (
										<ErrorMessage>{formState.errors.cidade.message}</ErrorMessage>
									)}
								</InputWrapper>
							</FormGrid>
						</FormSection>

						<FormSection>
							<FormSectionTitle>
								<FaUser />
								{t("nda.form.sections.signer")}
							</FormSectionTitle>
							<FormGrid columns={2}>
								<InputWrapper className={formState.errors.nomeResponsavel ? 'has-error' : ''}>
									<InputLabel>{t("nda.form.labels.nomeResponsavel")} *</InputLabel>
									<Input
										type="text"
										placeholder={t("nda.form.placeholders.nomeResponsavel")}
										className={formState.errors.nomeResponsavel ? 'error' : ''}
										{...register("nomeResponsavel")}
									/>
									{formState.errors.nomeResponsavel && (
										<ErrorMessage>{formState.errors.nomeResponsavel.message}</ErrorMessage>
									)}
								</InputWrapper>

								<InputWrapper className={formState.errors.emailResponsavel ? 'has-error' : ''}>
									<InputLabel>{t("nda.form.labels.emailResponsavel")} *</InputLabel>
									<Input
										type="email"
										placeholder={t("nda.form.placeholders.emailResponsavel")}
										className={formState.errors.emailResponsavel ? 'error' : ''}
										{...register("emailResponsavel")}
									/>
									{formState.errors.emailResponsavel && (
										<ErrorMessage>{formState.errors.emailResponsavel.message}</ErrorMessage>
									)}
								</InputWrapper>
							</FormGrid>
						</FormSection>

						<InfoBox>
							<p dangerouslySetInnerHTML={{ __html: t("nda.form.infoBox") }} />
						</InfoBox>

						<p style={{ fontSize: '13px', color: '#A3A6A6', marginTop: '16px', marginBottom: '0' }}>
							Ao enviar, você concorda com nossa{" "}
							<a href="/pt/privacy-policy" target="_blank" rel="noreferrer" style={{ color: '#A191FF', textDecoration: 'underline' }}>
								Política de Privacidade
							</a>.
						</p>

						<SubmitButton
							type="button"
							onClick={hookFormHandleSubmit(handleSubmit)}
							disabled={!formState.isValid || formState.isSubmitting}
						>
							{formState.isSubmitting ? (
								<>
									<AiOutlineLoading className="animate-spin" />
									{t("nda.form.submitting")}
								</>
							) : (
								<>
									<HiDocumentText />
									{t("nda.form.submitButton")}
								</>
							)}
						</SubmitButton>
					</form>
				</FormCard>
			</Container>
		</Section>
	);
}
